import type { NodeDataType } from './GraphType';
import type { SpeechType } from './NormalEdge';
import type { Node, NodeProps } from '@xyflow/react';
import React, { useState, useLayoutEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getEnvConfig } from '@configs/env';
import { ProviderIcon } from '@configs/providerIcons';
import styled from '@emotion/styled';
import { Avatar, IconButton, Tooltip } from '@mui/material';
import { Handle, Position, useReactFlow } from '@xyflow/react';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { useStore } from '@stores/RootStore';
import { fonts } from '@theme/fontsCustomer';
import { DangerFillIcon } from '@icons/DangerFillIcon';
import { PlayIcon } from '@icons/PlayIcon';
import { TrashIcon } from '@icons/TrashIcon';
import { WorkflowActionDTOModel } from '@repositories/workflowRepository/models/WorkflowActionDTOModel';
import { SelectNodeSpeech } from './SelectNodeSpeech';

// Node type with custom data for v12 compatibility
type ActionNodeType = Node<NodeDataType, 'action'>;
type Props = NodeProps<ActionNodeType>;

export const ActionNode = observer((node: Props) => {
  const { data, isConnectable: isNodeConnectable } = node;
  const { t } = useTranslation();
  const { workflowDetailStore } = useStore();
  const { SMP_PROVIDER_NAME } = getEnvConfig();
  const { fitView } = useReactFlow();
  const isEditable = workflowDetailStore.checkEditable();
  const isSelected = workflowDetailStore.curNodeId === node.id;
  const [showExceptionMenu, setShowExceptionMenu] = useState<boolean>(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const exceptionButtonRef = useRef<HTMLDivElement>(null);

  /**
   * 노드 추가 후 뷰 조정 (전체 보기로 이동)
   * 이중 requestAnimationFrame으로 DOM 업데이트 완료 후 실행
   */
  const adjustViewAfterNodeAdd = useCallback(() => {
    // 이중 rAF: 브라우저가 레이아웃 계산을 완료한 후 fitView 실행
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        fitView({ padding: 0.2, duration: 500 });
      });
    });
  }, [fitView]);

  // 현재 노드에 연결된 예외/조건 엣지 확인
  const exceptionEdge = workflowDetailStore.findEdgeBySource(node.id, 'exception');
  // 노드 연결 가능 여부
  const isConnectable = isNodeConnectable && isEditable;
  // 액션 이름 (조건 분기는 번역 적용)
  const actionName =
    data.actionModel?.type === 'IF_ACTION' ? t('Workflow_Create_IfAction') : data.actionModel?.actionName;
  /**
   * 메뉴 위치 계산
   */
  useLayoutEffect(() => {
    if (showExceptionMenu && exceptionButtonRef.current) {
      const buttonRect = exceptionButtonRef.current.getBoundingClientRect();
      const menuWidth = 272; // SelectNodeSpeech 메뉴 너비

      // 버튼의 가로 중앙 아래에 메뉴 배치
      const x = buttonRect.left + buttonRect.width / 2 - menuWidth / 2;
      const y = buttonRect.bottom + 10; // 버튼 아래 10px 간격

      setMenuPosition({ x, y });
    }
  }, [showExceptionMenu]);

  /**
   * 액션 선택 클릭
   * @param e 이벤트
   */
  const handleClickActionBehavior = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    workflowDetailStore.curNodeId = node.id;
    workflowDetailStore.setToolbarView(isEditable ? 'actionSelect' : 'actionConfig');
  };

  /**
   * 액션 실행 결과 클릭
   * @param e 이벤트
   */
  const handleClickActionExecResult = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    workflowDetailStore.curNodeId = node.id;
    workflowDetailStore.setToolbarView('actionExec');
  };

  /**
   * 액션 삭제 클릭
   * @param e 이벤트
   * @param node 노드
   */
  const handleClickTrash = (e: React.MouseEvent<HTMLButtonElement>, node: NodeProps) => {
    e.stopPropagation();
    workflowDetailStore.removeActionNode(node.id);
    workflowDetailStore.setToolbarView('');
  };

  /**
   * 노드 클릭
   */
  const handleClickNode = () => {
    workflowDetailStore.curNodeId = node.id;
    workflowDetailStore.setToolbarView(isEditable ? 'actionSelect' : 'actionConfig');
  };

  /**
   * 액션 설정이 완료되지 않았는지 체크
   */
  const isActionConfigInvalid = () => {
    const actionModel = data.actionModel;

    // 액션이 선택되지 않은 경우
    if (!actionModel || !actionModel.actionMethod) {
      return true;
    }

    // 일반 액션 (CONNECTION_ACTION, INNER_ACTION)인 경우
    // actionArguments 검사
    const actionArguments = actionModel.actionArguments;
    if (!actionArguments || actionArguments.length === 0) {
      // arguments가 없는 액션은 유효함
      return false;
    }

    // 필수 파라미터가 모두 채워져있는지 확인
    // expression이 비어있으면 invalid (빈 문자열도 invalid)
    return actionArguments.some(arg => {
      // expression이 없거나 빈 문자열인 경우
      if (!arg.expression || arg.expression.trim() === '') {
        return true;
      }
      return false;
    });
  };

  const showDangerIcon = isActionConfigInvalid();

  /**
   * 예외 액션 추가 버튼 클릭
   * @param e 이벤트
   */
  const handleClickActionExceptionBtn = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setShowExceptionMenu(prev => !prev);
  };

  /**
   * 메뉴에서 액션/조건 선택
   * @param e 이벤트
   * @param type 선택된 타입
   */
  const handleClickSpeech = (e: React.MouseEvent, type: SpeechType) => {
    e.stopPropagation();
    if (type === 'ACTION') {
      const newActionNode = workflowDetailStore.addExceptionActionNode(node.id);
      workflowDetailStore.curNodeId = newActionNode.id;
      workflowDetailStore.setToolbarView('actionSelect');
      adjustViewAfterNodeAdd();
    } else if (type === 'CONDITION') {
      // 조건 분기 액션 모델 생성
      const conditionActionModel = new WorkflowActionDTOModel({
        type: 'IF_ACTION',
        actionName: t('Workflow_Create_IfAction'),
        actionMethod: '',
        actionArguments: [
          {
            argumentName: 'expr',
            expression: '',
            expressionType: 'SIMPLE',
          },
        ],
      });

      // Exception 경로에 조건 노드 추가
      const newConditionNode = workflowDetailStore.addExceptionConditionNode(node.id, conditionActionModel);

      runInAction(() => {
        workflowDetailStore.curNodeId = newConditionNode.id;
        workflowDetailStore.setToolbarView('conditionSelect');
      });
      adjustViewAfterNodeAdd();
    }

    setShowExceptionMenu(false);
  };

  return (
    <Container isSelected={isSelected} onClick={handleClickNode}>
      <Type>
        <span>
          <PlayIcon width={16} height={16} />
          <span>{`${t('Workflow_Create_36')} ${data.actionIndex ?? 0}`} </span>
        </span>
        <TypeRight>
          {showDangerIcon && (
            <Tooltip title={t('Workflow_Validation_RequiredFieldsNotSet')} placement="bottom" arrow>
              <DangerIconWrapper>
                <DangerFillIcon width={16} height={16} />
              </DangerIconWrapper>
            </Tooltip>
          )}
          {isEditable && (
            <IconBtn onClick={e => handleClickTrash(e, node)}>
              <TrashIcon width={14} height={14} />
            </IconBtn>
          )}
        </TypeRight>
      </Type>
      <>
        <Behavior
          isSelected={isSelected && workflowDetailStore.toolbar === 'actionConfig'}
          hasActionName={!!actionName}
          onClick={handleClickActionBehavior}
        >
          <BehaviorTitleBox>
            {data.isSmpAction ? (
              <ProviderIcon providerName={SMP_PROVIDER_NAME} width={20} height={20} />
            ) : data.connectionModel?.subscription?.product.iconUrl ? (
              <Avatar
                src={data.connectionModel.subscription?.product.iconUrl}
                sx={{
                  width: '16px',
                  height: '16px',
                }}
                variant="square"
              />
            ) : null}
            {actionName || t('Workflow_Create_38')}
          </BehaviorTitleBox>
        </Behavior>

        {workflowDetailStore.execution && (
          <Config
            onClick={handleClickActionExecResult}
            isSelected={isSelected && workflowDetailStore.toolbar === 'actionExec'}
          >
            {t('Workflow.graph.execResult')}
          </Config>
        )}
      </>
      {isEditable && isSelected && !exceptionEdge && data.actionModel?.type === 'CONNECTION_ACTION' && (
        <SideButton>
          <ExceptionIcon ref={exceptionButtonRef} onClick={handleClickActionExceptionBtn}>
            예외 추가
          </ExceptionIcon>
        </SideButton>
      )}
      {/* 예외 액션/조건 선택 메뉴 */}
      {showExceptionMenu && <SelectNodeSpeech menuPosition={menuPosition} onClickSpeech={handleClickSpeech} />}

      <Handle
        type="source"
        position={Position.Bottom}
        id="normal"
        isConnectable={isConnectable}
        style={{
          width: 8,
          height: 8,
          background: '#fff',
          border: '2px solid #9CA3AF',
        }}
      />
      <Handle
        type="target"
        position={Position.Top}
        id="normal"
        isConnectable={isConnectable}
        style={{
          width: 8,
          height: 8,
          background: '#fff',
          border: '2px solid #9CA3AF',
        }}
      />
      {data.actionModel?.type === 'CONNECTION_ACTION' && (
        <Handle
          type="source"
          position={Position.Right}
          id="exception"
          isConnectable={isConnectable}
          style={{
            width: 8,
            height: 8,
            background: '#fff',
            border: '2px solid #9CA3AF',
          }}
        />
      )}
      {data.actionModel?.type === 'IF_ACTION' && (
        <Handle
          type="source"
          position={Position.Right}
          id="conditionThen"
          isConnectable={isConnectable}
          style={{
            width: 8,
            height: 8,
            background: '#fff',
            border: '2px solid #9CA3AF',
          }}
        />
      )}
    </Container>
  );
});

export const MemoizedMovie = React.memo(ActionNode);

const Container = styled.div<{ isSelected: boolean }>`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;

  border: ${props =>
    props.isSelected
      ? `2px solid ${props.theme.colors['border-primary']}`
      : `1px solid ${props.theme.colors['border-gray-lighter']}`};
  outline: ${props => (props.isSelected ? `4px solid ${props.theme.colors['border-primary-light']}` : 'none')};
  height: fit-content;
  width: '392px';
  background: ${props => props.theme.colors['bg-gray-light']};
  border-radius: 4px;
  padding: ${props => (props.isSelected ? '7px' : '8px')};
  box-shadow: ${props =>
    props.isSelected
      ? '0px 8px 10px -5px rgba(0, 0, 0, 0.08), 0px 16px 24px 2px rgba(0, 0, 0, 0.04), 0px 6px 30px 5px rgba(0, 0, 0, 0.05)'
      : 'none'};
`;

const Type = styled.div`
  display: flex;
  flex-direction: row;
  margin: 0px 4px;

  ${fonts.Headline9};
  align-items: center;
  margin-bottom: 4px;
  justify-content: space-between;
  & > span {
    display: flex;
    flex-direction: row;
    gap: 4px;
    align-items: center;
  }
`;

const TypeRight = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
  align-items: center;
`;

const DangerIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const Behavior = styled.div<{ isSelected: boolean; hasActionName: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 40px;
  border-radius: 3px;
  border-bottom-left-radius: ${props => (props.isSelected ? '0px' : '3px')};
  border-bottom-right-radius: ${props => (props.isSelected ? '0px' : '3px')};
  padding: 0px 16px;
  border: 1px solid ${props => props.theme.colors['border-gray-lighter']};
  background: ${props => props.theme.colors['bg-white']};

  ${props => (props.isSelected ? fonts.Headline8 : fonts.Body2)};
  color: ${props =>
    props.isSelected
      ? props.theme.colors['text-primary']
      : props.hasActionName
      ? props.theme.colors['text-gray-main']
      : props.theme.colors['text-gray-light']};
`;

const Config = styled.div<{ isSelected: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 40px;
  border-radius: 3px;
  border-top-left-radius: 0px;
  border-top-right-radius: 0px;
  padding: 0px 16px;
  border: 1px solid ${props => props.theme.colors['border-gray-lighter']};
  border-top: 0px;
  background: ${props => props.theme.colors['bg-white']};
  ${props => (props.isSelected ? fonts.Headline8 : fonts.Body2)};
  color: ${props => (props.isSelected ? props.theme.colors['text-primary'] : props.theme.colors['text-gray-main'])};
`;

const IconBtn = styled(IconButton)`
  width: 24px;
  height: 24px;
  background: ${props => props.theme.colors['bg-white']};
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  border-radius: 2px;
  border: 1px solid ${props => props.theme.colors['border-gray-lighter']};
`;

const SideButton = styled(IconButton)<{ borderColor?: string }>`
  position: absolute;
  top: 50%;
  right: -80px; /* 노드의 오른쪽 밖으로 약간 이동 */
  transform: translateY(-50%);

  padding: 0;
  background: ${props => props.theme.colors['bg-white']};
`;

const BehaviorTitleBox = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
`;

const ExceptionIcon = styled.div`
  display: flex;
  padding: 1px 12px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 12px;
  border: 1px solid ${props => props.theme.colors['border-gray-lighter']};
  background: ${props => props.theme.colors['bg-white']};
  color: ${props => props.theme.colors['text-gray-main']};
  font-size: 12px;
  height: fit-content;
  line-height: 1;
  pointer-events: all;
  width: fit-content;
  white-space: nowrap;
  cursor: pointer;
  :hover {
    border: 1px solid ${props => props.theme.colors['border-primary']};
    background-color: ${props => props.theme.colors['bg-primary-lighter']};
  }
`;
